/**
 * CareLogix™ ORACLE — Phase 36 · Global Intelligence Globe™ (2D)
 *
 * Constitutional invariants honored:
 *  - Consumes existing system endpoints only.
 *  - No fake datasets / no simulated opportunities.
 *  - When data is empty, the layer surfaces `NO_DATA_AVAILABLE`.
 *  - Funding outputs are advisory; explicit no-guarantee note shown.
 *
 * Visualization: lightweight 2D SVG world map (no external 3D deps).
 * 3D globe upgrade is intentionally deferred.
 */
import React, { useEffect, useMemo, useState } from "react";
import { Link } from "react-router-dom";

const BACKEND =
  (typeof process !== "undefined" && process.env && process.env.REACT_APP_BACKEND_URL) ||
  "";

// Region centroids used to plot data-driven dots. Coordinates are deliberately
// coarse (region-level) — we never imply precision we do not have.
const REGION_CENTROIDS = {
  "US":            { cx: 215, cy: 175, label: "United States" },
  "United States": { cx: 215, cy: 175, label: "United States" },
  "USA":           { cx: 215, cy: 175, label: "United States" },
  "Africa":        { cx: 510, cy: 280, label: "Africa" },
  "UK":            { cx: 475, cy: 145, label: "United Kingdom" },
  "UK/Global":     { cx: 475, cy: 145, label: "United Kingdom" },
  "Africa/Global": { cx: 510, cy: 280, label: "Africa" },
  "Global":        { cx: 500, cy: 200, label: "Global" },
  "Diaspora":      { cx: 380, cy: 200, label: "Diaspora" },
};

const LAYER_DEFS = [
  { key: "services",      label: "CareLogix Coverage" },
  { key: "foundations",   label: "Foundations Registry" },
  { key: "jurisdictions", label: "Jurisdiction Policies" },
  { key: "audit",         label: "Compliance Signals (24h)" },
];

function useFetch(path, defaultValue, { adminGated = false } = {}) {
  const [data, setData] = useState(defaultValue);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  useEffect(() => {
    let alive = true;
    (async () => {
      try {
        const headers = {};
        if (adminGated) {
          let token = null;
          try { token = window.localStorage.getItem("carelogix_adminone_token"); } catch { /* ignore */ }
          if (!token) {
            if (alive) { setData(null); setLoading(false); }
            return;
          }
          headers["Authorization"] = `Bearer ${token}`;
        }
        const r = await fetch(`${BACKEND}${path}`, { headers });
        if (!alive) return;
        if (!r.ok) {
          if (adminGated && (r.status === 401 || r.status === 403)) {
            setData(null); setLoading(false); return;
          }
          throw new Error(`HTTP ${r.status}`);
        }
        const j = await r.json();
        if (!alive) return;
        setData(j);
      } catch (e) {
        if (alive) setError(String(e.message || e));
      } finally {
        if (alive) setLoading(false);
      }
    })();
    return () => { alive = false; };
  }, [path, adminGated]);
  return { data, loading, error };
}

function Dot({ cx, cy, count, label, accent, testid }) {
  if (!cx || !cy) return null;
  const r = Math.min(14, 4 + Math.sqrt(Math.max(1, count)) * 2);
  return (
    <g data-testid={testid}>
      <circle cx={cx} cy={cy} r={r + 6} fill={accent} opacity="0.10" />
      <circle cx={cx} cy={cy} r={r}     fill={accent} opacity="0.55" />
      <circle cx={cx} cy={cy} r={Math.max(2, r/2.5)} fill="#0b0f14" />
      <text x={cx + r + 6} y={cy + 4} fontSize="10" fill="#dfe3eb">{label}: {count}</text>
    </g>
  );
}

export default function IntelligenceGlobe() {
  const services      = useFetch("/api/oracle/services/registry", null);
  const foundations   = useFetch("/api/oracle/funding/crm/foundations", null);
  const jurisdictions = useFetch("/api/oracle/jurisdiction/policies", null);
  const audit         = useFetch("/api/adminone/enterprise-audit/summary", null, { adminGated: true });
  const compliance    = useFetch("/api/adminone/governance/oracle-compliance", null, { adminGated: true });
  const contact       = useFetch("/api/oracle/funding/crm/contact", null);

  const [enabledLayers, setEnabledLayers] = useState({
    services: true,
    foundations: true,
    jurisdictions: true,
    audit: true,
  });

  const toggle = (key) => setEnabledLayers((s) => ({ ...s, [key]: !s[key] }));

  // Aggregate counts by region — truthful only.
  const layerCounts = useMemo(() => {
    const acc = {
      services:      {},
      foundations:   {},
      jurisdictions: {},
      audit:         {},
    };
    // Services
    (services.data?.services || []).forEach((s) => {
      const region = s.kind === "internal" ? "Global" : "Global";
      acc.services[region] = (acc.services[region] || 0) + 1;
    });
    // Foundations
    (foundations.data?.foundations || []).forEach((f) => {
      const region = f.region || "Global";
      acc.foundations[region] = (acc.foundations[region] || 0) + 1;
    });
    // Jurisdictions
    (jurisdictions.data?.policies || []).forEach((p) => {
      const region = p.scope || "Global";
      acc.jurisdictions[region] = (acc.jurisdictions[region] || 0) + 1;
    });
    // Audit — global aggregate (no per-region telemetry exists; we surface Global only)
    if (audit.data?.total_events != null) {
      acc.audit["Global"] = audit.data.total_events || 0;
    }
    return acc;
  }, [services.data, foundations.data, jurisdictions.data, audit.data]);

  const layerHasData = (k) => {
    const map = layerCounts[k] || {};
    return Object.values(map).some((v) => v > 0);
  };

  return (
    <main style={styles.page} data-testid="intelligence-globe-page">
      <header style={styles.header}>
        <div>
          <div style={styles.eyebrow}>PHASE 36 · GLOBAL INTELLIGENCE GLOBE™</div>
          <h1 style={styles.title}>Constitutional Intelligence — World View</h1>
          <p style={styles.sub}>
            Data-driven only. No simulated opportunities. Empty layers display{" "}
            <code style={styles.code}>NO_DATA_AVAILABLE</code>. This view is research
            and decision-support — not advice.
          </p>
        </div>
        <div style={styles.headerLinks}>
          {(() => { try { return !!window.localStorage.getItem("carelogix_adminone_token"); } catch { return false; } })() && (
            <Link to="/adminone/telemetry" style={styles.link} data-testid="link-telemetry">AdminOne Telemetry →</Link>
          )}
          <Link to="/oracle/funding" style={styles.link} data-testid="link-funding">Funding Command →</Link>
        </div>
      </header>

      <section style={styles.kpiRow} data-testid="globe-kpi-row">
        <KPI label="Compliance Status"  value={compliance.data?.compliance_status || "—"}   sub="ORACLE" />
        <KPI label="Compliance Rate"    value={`${compliance.data?.compliance_rate ?? 0}%`} sub="acknowledged @ current version" />
        <KPI label="Current Version"    value={compliance.data?.current_version || "—"}    sub="ORACLE version" />
        <KPI label="Audit Events · 24h" value={audit.data?.total_events ?? 0}              sub="across all domains" />
      </section>

      <section style={styles.controls} data-testid="globe-layer-controls">
        {LAYER_DEFS.map((l) => (
          <button
            key={l.key}
            type="button"
            onClick={() => toggle(l.key)}
            style={{
              ...styles.toggle,
              opacity: enabledLayers[l.key] ? 1 : 0.45,
              borderColor: enabledLayers[l.key] ? "#c5a059" : "#374151",
              color: enabledLayers[l.key] ? "#c5a059" : "#94a3b8",
            }}
            data-testid={`globe-toggle-${l.key}`}
          >
            {enabledLayers[l.key] ? "● " : "○ "}{l.label}{" "}
            {!layerHasData(l.key) && <span style={styles.noData}>NO_DATA_AVAILABLE</span>}
          </button>
        ))}
      </section>

      <section style={styles.mapWrap} data-testid="globe-svg-wrap">
        <svg viewBox="0 0 800 420" style={styles.svg} role="img" aria-label="Global Intelligence Map (2D)">
          {/* Ocean */}
          <rect x="0" y="0" width="800" height="420" fill="#0b1220" />
          {/* Latitude/longitude guide grid */}
          {[60, 120, 180, 240, 300, 360].map((y) => (
            <line key={`h-${y}`} x1="0" y1={y} x2="800" y2={y} stroke="#1f2937" strokeWidth="0.5" />
          ))}
          {[100, 200, 300, 400, 500, 600, 700].map((x) => (
            <line key={`v-${x}`} x1={x} y1="0" x2={x} y2="420" stroke="#1f2937" strokeWidth="0.5" />
          ))}
          {/* Coarse continent shapes — schematic, not for navigation */}
          <g stroke="#2a3441" strokeWidth="0.6" fill="#10192a">
            {/* North America */}
            <path d="M120,90 L260,90 L290,150 L260,230 L180,260 L130,210 L100,140 Z" />
            {/* South America */}
            <path d="M230,260 L290,260 L300,340 L260,400 L230,370 Z" />
            {/* Europe */}
            <path d="M430,90 L520,90 L530,150 L480,170 L430,150 Z" />
            {/* Africa */}
            <path d="M460,180 L560,180 L580,300 L520,370 L470,340 L450,250 Z" />
            {/* Asia */}
            <path d="M540,90 L740,90 L760,200 L660,240 L560,210 L530,150 Z" />
            {/* Australia */}
            <path d="M660,310 L760,310 L770,360 L680,370 Z" />
          </g>

          {/* Plotted layers */}
          {enabledLayers.services && Object.entries(layerCounts.services).map(([region, count]) => {
            const c = REGION_CENTROIDS[region];
            if (!c) return null;
            return <Dot key={`svc-${region}`} cx={c.cx} cy={c.cy} count={count} label={`Services (${c.label})`} accent="#7AC860" testid={`globe-dot-services-${region}`} />;
          })}
          {enabledLayers.foundations && Object.entries(layerCounts.foundations).map(([region, count]) => {
            const c = REGION_CENTROIDS[region];
            if (!c) return null;
            return <Dot key={`fnd-${region}`} cx={c.cx} cy={c.cy + 18} count={count} label={`Foundations (${c.label})`} accent="#c5a059" testid={`globe-dot-foundations-${region}`} />;
          })}
          {enabledLayers.jurisdictions && Object.entries(layerCounts.jurisdictions).map(([region, count]) => {
            const c = REGION_CENTROIDS[region];
            if (!c) return null;
            return <Dot key={`jur-${region}`} cx={c.cx + 18} cy={c.cy} count={count} label={`Jurisdiction (${c.label})`} accent="#60B8E8" testid={`globe-dot-jurisdictions-${region}`} />;
          })}
          {enabledLayers.audit && Object.entries(layerCounts.audit).map(([region, count]) => {
            const c = REGION_CENTROIDS[region];
            if (!c) return null;
            return <Dot key={`aud-${region}`} cx={c.cx - 18} cy={c.cy - 18} count={count} label={`Audit 24h (${c.label})`} accent="#E5484D" testid={`globe-dot-audit-${region}`} />;
          })}
        </svg>
      </section>

      <section style={styles.legend} data-testid="globe-legend">
        <LegendChip color="#7AC860" label="CareLogix services" />
        <LegendChip color="#c5a059" label="Foundations registry" />
        <LegendChip color="#60B8E8" label="Jurisdiction policies" />
        <LegendChip color="#E5484D" label="Compliance signals (audit 24h)" />
      </section>

      <section style={styles.panel} data-testid="globe-contact-panel">
        <header style={styles.panelHead}>
          <h2 style={styles.panelTitle}>Capital & Funding Command — Contact</h2>
          <span style={styles.panelMeta}>Advisory only · no guarantees</span>
        </header>
        {contact.data?.primary_contact ? (
          <div style={styles.contactGrid}>
            <KV k="Label"   v={contact.data.primary_contact.label} />
            <KV k="Email"   v={contact.data.primary_contact.email} />
            <KV k="Phone"   v={contact.data.primary_contact.phone} />
            <KV k="Notice"  v={contact.data.constitutional_note} />
          </div>
        ) : (
          <div style={styles.sub}>NO_DATA_AVAILABLE</div>
        )}
      </section>

      <footer style={styles.footer}>
        CareLogix™ ORACLE · Global Intelligence Globe (2D) · English-canonical.
      </footer>
    </main>
  );
}

function KPI({ label, value, sub }) {
  return (
    <div style={styles.kpi} data-testid={`globe-kpi-${String(label).toLowerCase().replace(/[^a-z0-9]+/g,'-')}`}>
      <div style={styles.kpiLbl}>{label}</div>
      <div style={styles.kpiVal}>{value}</div>
      <div style={styles.kpiSub}>{sub}</div>
    </div>
  );
}

function KV({ k, v }) {
  return (
    <div style={styles.kvRow}>
      <span style={styles.kvK}>{k}</span>
      <span style={styles.kvV}>{v}</span>
    </div>
  );
}

function LegendChip({ color, label }) {
  return (
    <span style={styles.legendChip}>
      <span style={{ ...styles.legendDot, background: color }} />
      {label}
    </span>
  );
}

const styles = {
  page: {
    minHeight: "100vh",
    background: "linear-gradient(180deg, #06090f 0%, #0b1018 100%)",
    color: "#dfe3eb",
    padding: "32px 24px 60px",
    fontFamily: "'Figtree', system-ui, -apple-system, Segoe UI, Roboto, sans-serif",
  },
  header: { display: "flex", justifyContent: "space-between", alignItems: "flex-start", gap: 24, marginBottom: 18 },
  eyebrow: { color: "#c5a059", letterSpacing: "0.2em", fontSize: 10.5, fontFamily: "'Space Mono', monospace" },
  title:   { fontSize: 28, margin: "6px 0 4px", color: "#f5e9c8", letterSpacing: "0.01em" },
  sub:     { fontSize: 13, color: "#94a3b8", maxWidth: 760, lineHeight: 1.55 },
  code:    { padding: "1px 6px", borderRadius: 4, background: "rgba(197,160,89,0.12)", color: "#c5a059", fontFamily: "'Space Mono', monospace", fontSize: 11 },
  headerLinks: { display: "flex", flexDirection: "column", gap: 6, alignItems: "flex-end" },
  link: { color: "#c5a059", textDecoration: "none", fontSize: 12, letterSpacing: "0.05em" },

  kpiRow: { display: "grid", gridTemplateColumns: "repeat(auto-fit, minmax(180px, 1fr))", gap: 12, marginBottom: 18 },
  kpi:    { padding: "12px 14px", border: "1px solid #1f2937", borderRadius: 10, background: "rgba(255,255,255,0.02)" },
  kpiLbl: { fontSize: 10.5, color: "#94a3b8", letterSpacing: "0.16em", textTransform: "uppercase" },
  kpiVal: { fontSize: 22, color: "#f5e9c8", marginTop: 4, fontWeight: 600 },
  kpiSub: { fontSize: 11, color: "#6b7280", marginTop: 2 },

  controls: { display: "flex", flexWrap: "wrap", gap: 8, marginBottom: 14 },
  toggle:   { padding: "6px 10px", borderRadius: 999, border: "1px solid #c5a059", background: "transparent", fontSize: 11.5, cursor: "pointer", letterSpacing: "0.05em", display: "inline-flex", alignItems: "center", gap: 6 },
  noData:   { color: "#E5484D", fontFamily: "'Space Mono', monospace", fontSize: 9.5 },

  mapWrap: { border: "1px solid #1f2937", borderRadius: 12, overflow: "hidden", marginBottom: 12, background: "#06090f" },
  svg:     { display: "block", width: "100%", height: "auto" },

  legend: { display: "flex", flexWrap: "wrap", gap: 12, marginBottom: 18, fontSize: 12, color: "#94a3b8" },
  legendChip: { display: "inline-flex", alignItems: "center", gap: 6 },
  legendDot:  { display: "inline-block", width: 10, height: 10, borderRadius: "50%" },

  panel: { border: "1px solid #1f2937", borderRadius: 12, padding: "16px 18px", marginBottom: 18, background: "rgba(255,255,255,0.02)" },
  panelHead: { display: "flex", justifyContent: "space-between", alignItems: "center", marginBottom: 8 },
  panelTitle:{ fontSize: 14, color: "#f5e9c8", margin: 0 },
  panelMeta: { fontSize: 11, color: "#94a3b8" },
  contactGrid: { display: "grid", gridTemplateColumns: "repeat(auto-fit, minmax(220px, 1fr))", gap: 8 },
  kvRow: { display: "flex", flexDirection: "column", gap: 2, padding: "6px 0", borderBottom: "1px solid rgba(255,255,255,0.05)" },
  kvK:   { fontSize: 10.5, color: "#94a3b8", letterSpacing: "0.12em", textTransform: "uppercase" },
  kvV:   { fontSize: 12.5, color: "#dfe3eb", lineHeight: 1.45 },

  footer: { fontSize: 10.5, color: "#6b7280", marginTop: 16, lineHeight: 1.6 },
};
